import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { LayoutDashboard, BookOpen, Landmark, Wallet, HandCoins, Receipt, ReceiptText, Boxes, Building2, PiggyBank, Users, FileBarChart2, ClipboardCheck, ShieldCheck, ScrollText, Settings, LogOut, ArrowLeft, Menu, X, PlusCircle, Building, Target, Scale, PieChart, FileStack, Percent, Smartphone, Warehouse, FileSearch, Clock, ChevronRight } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import NotificationBell from "../../components/hris/NotificationBell";
import { useApakahDesktop } from "../../hooks/useApakahDesktop";
import { HALAMAN_DASHBOARD, berandaMemantulKe } from "../../lib/halamanAwal";
import { HALAMAN_PP, JENIS, LABEL_JENIS, PATH_PROGRAM, pathHalaman } from "./pp/halamanPP";

// Menu 3 tingkat Price Protection (brief owner 22 Sep 2026 hal. 1–2):
// Program Price Protection › Program Support Aktivasi / Program Support Cashback ›
// ZOA GROUP / Dealer Partner. Daun disusun dari peta halaman yang SAMA dengan
// rutenya (`pp/halamanPP.js::HALAMAN_PP` → `pathHalaman`), jadi label menu,
// judul halaman, dan URL tidak bisa berpisah jalan. Induk hanya membuka/menutup.
// `data-testid` lama `fin-nav-cashback-program` pindah ke tombol induk.
// Daun pertama "Semua Program" (tinjauan PP r1): seluruh program produksi hari
// ini adalah program LAMA tanpa kanal — tanpa daun ini dashboard/daftar/laporan
// tanpa saringan (satu-satunya tempat funnel, hangus, dan recovery program lama
// yang uangnya berjalan) hanya terbuka lewat tautan notifikasi. `end`: rute
// halaman level 3 berada DI BAWAH path ini, dan daun ini tidak boleh ikut disorot.
// Daun ini juga yang membuka induknya untuk rute lama (tautan notifikasi, Pusat
// Laporan) — dulu lewat medan `awalan` induk, kini mubazir dan dicabut.
const GRUP_PRICE_PROTECTION = {
  kunci: "pp", icon: Percent, label: "Program Price Protection", tid: "fin-nav-cashback-program",
  anak: [{ to: PATH_PROGRAM, end: true, label: "Semua Program", tid: "fin-nav-pp-semua", modKey: "finance_cashback_claim" },
    ...[JENIS.AKTIVASI, JENIS.CASHBACK].map((jenis) => ({
    kunci: `pp-${jenis}`, label: `Program ${LABEL_JENIS[jenis]}`, tid: `fin-nav-pp-${jenis}`,
    anak: HALAMAN_PP.filter((h) => h.jenis === jenis).map((h) => ({
      to: pathHalaman(h), label: h.label, tid: `fin-nav-pp-${h.slugJenis}-${h.slugKanal}`, modKey: "finance_cashback_claim",
    })),
  }))],
};

const NAV = [
  { section: "Utama", items: [
    { to: "/finance", end: true, icon: LayoutDashboard, label: "Dashboard", tid: "fin-nav-dashboard", modKey: "finance_dashboard" },
    { to: "/finance/catat", icon: PlusCircle, label: "Catat Cepat", tid: "fin-nav-catat", accent: true, modKey: "finance_catat" },
  ]},
  { section: "Kas & Bank", items: [
    { to: "/finance/kas-bank", icon: Wallet, label: "Kas & Bank", tid: "fin-nav-kasbank", modKey: "finance_kas_bank" },
    { to: "/finance/petty-cash", icon: PiggyBank, label: "Petty Cash", tid: "fin-nav-petty", modKey: "finance_petty" },
    { to: "/finance/rekonsiliasi", icon: Landmark, label: "Rekonsiliasi Bank", tid: "fin-nav-rekon", modKey: "finance_rekonsiliasi" },
    { to: "/finance/cash-opname", icon: Scale, label: "Cash Opname", tid: "fin-nav-opname", modKey: "finance_cash_opname" },
  ]},
  { section: "Piutang & Utang", items: [
    { to: "/finance/piutang", icon: HandCoins, label: "Piutang (AR)", tid: "fin-nav-piutang", modKey: "finance_piutang" },
    { to: "/finance/utang", icon: Receipt, label: "Utang (AP)", tid: "fin-nav-utang", modKey: "finance_utang" },
    { to: "/finance/credit-note", icon: FileStack, label: "Credit Note", tid: "fin-nav-credit-note", modKey: "finance_credit_note" },
    // IMEI Cashback / Price Protection (batch 11 Agu 2026) — SATU kunci izin
    // `finance_cashback_claim` menaungi halaman Program (kini empat halaman lewat
    // menu 3 tingkat) & Claim Batch, pola sama dengan `inventory_po` yang
    // menaungi 4 halaman (lihat PETA-MODUL.md).
    GRUP_PRICE_PROTECTION,
    { to: "/finance/cashback/claims", icon: Smartphone, label: "Cashback Claim (IMEI)", tid: "fin-nav-cashback-claim", modKey: "finance_cashback_claim" },
    // Claim MDR (brief 22 Sep butir 1) — kunci izinnya SENDIRI
    // (`finance_claim_mdr`), bukan ikut `finance_cashback_claim`: datanya
    // memang bersinggungan lewat Price Protection, tapi yang mengerjakannya
    // bisa orang lain, dan menyatukan kuncinya membuat satu izin membuka dua
    // modul uang sekaligus.
    { to: "/finance/claim-mdr", icon: FileSearch, label: "Claim MDR", tid: "fin-nav-claim-mdr", modKey: "finance_claim_mdr" },
    { to: "/finance/beban", icon: Receipt, label: "Beban Usaha", tid: "fin-nav-beban", modKey: "finance_beban" },
    { to: "/finance/reimbursement", icon: ReceiptText, label: "Reimbursement", tid: "fin-nav-reimbursement", modKey: "finance_reimbursement" },
  ]},
  { section: "Aset & Persediaan", items: [
    { to: "/finance/persediaan", icon: Boxes, label: "Persediaan", tid: "fin-nav-persediaan", modKey: "finance_persediaan" },
    { to: "/finance/aset-tetap", icon: Building, label: "Aset Tetap", tid: "fin-nav-aset", modKey: "finance_aset_tetap" },
    { to: "/finance/dividen", icon: Users, label: "Dividen", tid: "fin-nav-dividen", modKey: "finance_dividen" },
  ]},
  { section: "Laporan & Kontrol", items: [
    { to: "/finance/laporan", icon: FileBarChart2, label: "Laporan Keuangan", tid: "fin-nav-laporan", modKey: "finance_laporan" },
    { to: "/finance/buku-besar", icon: BookOpen, label: "Buku Besar", tid: "fin-nav-buku-besar", modKey: "finance_laporan" },
    { to: "/finance/jurnal", icon: FileSearch, label: "Jurnal per Transaksi", tid: "fin-nav-jurnal", modKey: "finance_laporan" },
    { to: "/finance/laba-kategori", icon: PieChart, label: "Laba per Kategori", tid: "fin-nav-laba-kategori", modKey: "finance_laporan" },
    { to: "/finance/laba-gudang", icon: Warehouse, label: "Laba per Gudang", tid: "fin-nav-laba-gudang", modKey: "finance_laporan" },
    { to: "/finance/budget", icon: Target, label: "Budget vs Actual", tid: "fin-nav-budget", modKey: "finance_budget" },
    { to: "/finance/coa", icon: BookOpen, label: "Chart of Accounts", tid: "fin-nav-coa", modKey: "finance_coa" },
    { to: "/finance/approval", icon: ClipboardCheck, label: "Approval SPV", tid: "fin-nav-approval", modKey: "finance_approval" },
    { to: "/finance/audit", icon: ShieldCheck, label: "Audit Trail", tid: "fin-nav-audit", modKey: "finance_audit" },
    { to: "/finance/pengaturan", icon: Settings, label: "Pengaturan", tid: "fin-nav-pengaturan", modKey: "finance_pengaturan" },
  ]},
];

// Saringan izin REKURSIF: daun tampil kalau `can(modKey, "view")`; induk tampil
// hanya kalau masih punya anak yang tampil — induk tanpa anak adalah tombol yang
// membuka daftar kosong.
function saringNav(item, can) {
  if (!item.anak) return can(item.modKey, "view") ? item : null;
  const anak = item.anak.map((a) => saringNav(a, can)).filter(Boolean);
  return anak.length ? { ...item, anak } : null;
}

function cocokRute(pathname, to) {
  return pathname === to || pathname.startsWith(`${to}/`);
}

// Kunci induk yang memuat rute aktif (leluhur daun yang cocok) — dibuka otomatis supaya halaman yang sedang dibuka selalu terlihat
// di menu (brief hal. 2: induk & submenu relevan expanded, daun di-highlight).
function indukAktif(items, pathname) {
  const out = [];
  const jalan = (item, leluhur) => {
    if (!item.anak) {
      if (cocokRute(pathname, item.to)) out.push(...leluhur);
      return;
    }
    item.anak.forEach((a) => jalan(a, [...leluhur, item.kunci]));
  };
  items.forEach((it) => jalan(it, []));
  return out;
}

// Satu induk menu (tingkat 1 atau 2). Klik = buka/tutup; tidak berpindah
// halaman. Animasi tinggi lewat grid-rows 0fr↔1fr (tinggi isi tidak perlu
// diketahui); `inert` saat tertutup supaya tautan tersembunyi tidak bisa
// difokus Tab. Tanpa interaksi hover-only (perangkat sentuh, CLAUDE.md 4 Sep).
function NavGrup({ item, tingkat, terbuka, onToggle, pathname }) {
  const buka = terbuka.has(item.kunci);
  const aktif = indukAktif([item], pathname).length > 0;
  const Icon = item.icon;
  const idIsi = `fin-nav-isi-${item.kunci}`;
  return (
    <div>
      <button
        type="button" data-testid={item.tid} aria-expanded={buka} aria-controls={idIsi}
        onClick={() => onToggle(item.kunci)}
        className={`w-full flex items-center gap-3 pl-3 pr-2 ${tingkat === 0 ? "py-2.5 text-sm" : "py-2 text-[13px]"} rounded-lg text-left transition-colors ${
          aktif ? "text-white font-medium" : "text-white/60 hover:text-white hover:bg-white/5"
        }`}
      >
        {Icon && <Icon className="w-4 h-4 shrink-0" />}
        {/* Label + chevron dalam satu kelompok bercelah kecil: sidebar w-64 hanya
            muat "Program Price Protection" satu baris kalau chevron tidak ikut
            memakan celah gap-3 (diukur di Chrome: perlu 160px). */}
        <span className="flex-1 min-w-0 flex items-center gap-1">
          <span className="flex-1 min-w-0 truncate tracking-tight">{item.label}</span>
          <ChevronRight data-chevron="" aria-hidden="true"
                        className={`w-3.5 h-3.5 shrink-0 transition-transform duration-200 motion-reduce:transition-none ${buka ? "rotate-90" : ""}`} />
        </span>
      </button>
      <div
        id={idIsi} data-testid={`${item.tid}-isi`} inert={!buka}
        className={`grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none ${buka ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
      >
        <div className="overflow-hidden">
          <div className="ml-3 pl-2 border-l border-white/10 space-y-0.5 py-0.5">
            {item.anak.map((a) => (a.anak ? (
              <NavGrup key={a.kunci} item={a} tingkat={tingkat + 1} terbuka={terbuka} onToggle={onToggle} pathname={pathname} />
            ) : (
              <NavLink
                key={a.to} to={a.to} end={a.end} data-testid={a.tid} title={a.label}
                className={({ isActive }) =>
                  `block px-3 py-2 rounded-lg text-[13px] leading-snug transition-colors ${
                    isActive ? "bg-white/10 text-white font-medium" : "text-white/60 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                {a.label}
              </NavLink>
            )))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function FinanceLayout() {
  // branding dari AuthContext — jangan fetch /config/branding sendiri di sini
  // (dulu jadi request duplikat ketiga di halaman finance; brief bos, laporan performa).
  const { user, logout, can, canWidget, branding } = useAuth();
  const nav = useNavigate();
  const queryClient = useQueryClient();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Breakpoint yang SAMA dengan sidebar/topbar — satu lonceng ter-mount, bukan dua.
  const isDesktop = useApakahDesktop();
  // Bersihkan cache React Query sebelum keluar: tombol ini berpindah tanpa memuat
  // ulang halaman, jadi tanpa clear() pengguna berikutnya di tab yang sama masih
  // memegang data keuangan pengguna sebelumnya. Sama dengan doLogout di Layout.jsx.
  const doLogout = () => { queryClient.clear(); logout(); nav("/login"); };
  // "Kembali ke HRIS" disembunyikan HANYA kalau "/" akan memantul balik ke Finance —
  // aturan yang sama dengan halaman awal (lib/halamanAwal.js), bukan tebakan dari role.
  // Dulu tombol ini disembunyikan untuk semua role finance_*, sementara admin bertemplate
  // Finance yang kini mendarat di sini justru BUTUH tombol ini untuk ke Dashboard Sales.
  const kembaliMemantul = berandaMemantulKe(user, can, canWidget) === HALAMAN_DASHBOARD.finance;
  // Filter NAV: hanya tampilkan item yang user boleh lihat (can(modKey, "view")),
  // rekursif untuk menu bertingkat.
  const visibleNav = NAV
    .map(sec => ({ ...sec, items: sec.items.map(it => saringNav(it, can)).filter(Boolean) }))
    .filter(sec => sec.items.length > 0);
  // Induk menu bertingkat yang terbuka. Rute aktif membuka leluhurnya sendiri
  // setiap kali lokasi berubah; buka/tutup manual tetap berlaku sampai pengguna
  // berpindah ke halaman di bawah induk yang ia tutup.
  const { pathname } = useLocation();
  const [terbuka, setTerbuka] = useState(() => new Set(indukAktif(NAV.flatMap((s) => s.items), pathname)));
  useEffect(() => {
    const aktif = indukAktif(NAV.flatMap((s) => s.items), pathname);
    if (aktif.length) setTerbuka((t) => (aktif.every((k) => t.has(k)) ? t : new Set([...t, ...aktif])));
  }, [pathname]);
  const toggleGrup = (kunci) => setTerbuka((t) => {
    const n = new Set(t);
    if (n.has(kunci)) n.delete(kunci); else n.add(kunci);
    return n;
  });

  return (
    <div className="min-h-screen flex bg-[#F8F9FA]">
      <aside
        data-testid="finance-sidebar"
        className={`fixed lg:sticky top-0 left-0 z-40 w-64 h-screen shrink-0 bg-[#0F172A] text-white flex flex-col transition-transform duration-300 lg:translate-x-0 ${drawerOpen ? "translate-x-0" : "-translate-x-full"}`}
      >
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-700 grid place-items-center shrink-0 shadow-lg shadow-emerald-500/25">
              <Landmark className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <p className="font-display font-bold text-sm leading-tight truncate">Finance</p>
              <p className="text-[11px] text-white/50">{branding.app_name || "ZOA OS"}</p>
            </div>
          </div>
          <button onClick={() => setDrawerOpen(false)} data-testid="fin-close-drawer" className="lg:hidden p-1.5 rounded-md hover:bg-white/10 text-white/60 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        <nav className="flex-1 px-3 py-3 space-y-4 overflow-y-auto">
          {/* Dipindah ke atas (brief owner 19 Agu poin 7) — dulu duduk di footer
              dekat tombol Keluar, di bawah SEMUA menu Finance, jadi staf yang cuma
              mampir sebentar harus scroll melewati seluruh sidebar untuk kembali ke
              HRIS. Sekarang jadi hal pertama yang terlihat begitu sidebar dibuka. */}
          {!kembaliMemantul && (
            <button
              data-testid="fin-back-hris"
              onClick={() => nav("/")}
              className="w-full inline-flex items-center gap-2 px-3 h-9 rounded-lg bg-white/5 hover:bg-white/10 text-white/70 hover:text-white text-sm transition-colors border border-white/10"
            >
              <ArrowLeft className="w-4 h-4" />
              Kembali ke HRIS
            </button>
          )}
          {/* Absensi Saya (G8 butir b): akun yang kini mendarat di Finance sebagai
              halaman awal tidak boleh kehilangan jalan satu klik ke clock-in harian —
              sidebar utama (Layout.jsx) yang biasa memuatnya tidak dirender di sini.
              Syaratnya sama dengan menu Portal di Layout: tertaut karyawan + izin. */}
          {!!user?.employee_id && can("my_attendance", "view") && (
            <NavLink
              to="/my-attendance" data-testid="fin-nav-my-attendance"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors text-white/60 hover:text-white hover:bg-white/5"
            >
              <Clock className="w-4 h-4 shrink-0" />
              <span className="truncate">Absensi Saya</span>
            </NavLink>
          )}
          {visibleNav.map(({ section, items }) => (
            <div key={section}>
              <p className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-wider text-white/40">{section}</p>
              <div className="space-y-0.5">
                {items.map((item) => item.anak ? (
                  <NavGrup key={item.kunci} item={item} tingkat={0} terbuka={terbuka} onToggle={toggleGrup} pathname={pathname} />
                ) : (
                  <NavLink
                    key={item.to} to={item.to} end={item.end} data-testid={item.tid}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                        item.accent
                          ? "bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 font-medium border border-emerald-500/30"
                          : isActive
                            ? "bg-white/10 text-white font-medium"
                            : "text-white/60 hover:text-white hover:bg-white/5"
                      }`
                    }
                  >
                    <item.icon className="w-4 h-4 shrink-0" />
                    <span className="truncate">{item.label}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="px-3 py-3 border-t border-white/10 space-y-1.5">
          <div className="flex items-center gap-2 px-3 py-2">
            <div className="w-9 h-9 rounded-full bg-emerald-600/20 text-emerald-300 grid place-items-center text-sm font-semibold">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-[11px] text-white/50 capitalize">{user?.role?.replace(/_/g, " ")}</p>
            </div>
            {/* Lonceng notifikasi (G8 butir b): akun yang mendarat di Finance sebagai
                halaman awal tidak boleh berhenti menerima notifikasi hanya karena layout
                ini tidak punya lonceng. Pola satu-instance sama dengan Layout.jsx:
                sidebar di desktop atau saat drawer terbuka (topbar mobile tertutup
                backdrop), topbar di layar kecil. */}
            {(isDesktop || drawerOpen) && <NotificationBell dark />}
          </div>
          <button
            data-testid="fin-btn-logout"
            onClick={() => { if (confirm("Keluar dari akun?")) doLogout(); }}
            className="w-full inline-flex items-center gap-2 px-3 py-2.5 rounded-lg bg-rose-600/10 hover:bg-rose-600/25 text-rose-300 hover:text-rose-100 text-sm font-medium transition-colors border border-rose-500/20"
          >
            <LogOut className="w-4 h-4" />
            Keluar
          </button>
        </div>
      </aside>

      {drawerOpen && <div onClick={() => setDrawerOpen(false)} className="fixed inset-0 z-30 bg-black/50 backdrop-blur-sm lg:hidden" />}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="lg:hidden sticky top-0 z-20 bg-white border-b border-slate-200 flex items-center gap-3 px-4 py-3">
          <button onClick={() => setDrawerOpen(true)} data-testid="fin-open-drawer" className="p-2 -ml-2 rounded-md hover:bg-slate-100 text-slate-700">
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="w-7 h-7 rounded-md bg-gradient-to-br from-emerald-500 to-emerald-700 grid place-items-center shrink-0">
              <Landmark className="w-4 h-4 text-white" />
            </div>
            <p className="font-display font-bold text-sm text-slate-900 truncate">Finance</p>
          </div>
          {!isDesktop && <NotificationBell />}
        </header>
        <main className="flex-1 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
